// Choice-question parsing and answer-formatting — spec §1/§2 of the Choice
// Questions contract. Mirrors the parse algorithm exactly across every
// platform (server, iOS, Android, web) so a hint parses identically
// everywhere.
//
//   "(Choice: Small | Medium | Large)"          single select
//   "(Choices: Cheese | Pepperoni | Mushroom)"  multi select (pick one or more)
//   "(Choice, other: Small | Medium | Large)"   single select + "Other"
//   "(Choices, other: A | B | C)"               multi select + "Other"

export interface ParsedChoice {
  multi: boolean;
  allowOther: boolean;
  options: string[];
}

/** Parse a question's type hint as a Choice question, or null if it isn't one. */
export function parseChoice(hint: string | undefined | null): ParsedChoice | null {
  if (!hint) return null;

  // 1. Trim; strip ONE leading "(" and ONE trailing ")" if present; trim.
  let t = String(hint).trim();
  if (t.startsWith('(')) t = t.slice(1);
  if (t.endsWith(')')) t = t.slice(0, -1);
  t = t.trim();

  // 2. Split at the FIRST ":" -> head, rest. No ":" => not a choice.
  const colonIndex = t.indexOf(':');
  if (colonIndex === -1) return null;
  const head = t.slice(0, colonIndex);
  const rest = t.slice(colonIndex + 1);

  // 3. h = head lowercased, trimmed. It is a choice iff h starts with "choice".
  const h = head.toLowerCase().trim();
  if (!h.startsWith('choice')) return null;

  const multi = h.startsWith('choices');
  const allowOther = h.includes('other');

  // 4. options = rest split on "|", each trimmed, drop empty, de-dupe
  // case-insensitively (keep first). Order preserved.
  const seen = new Set<string>();
  const options: string[] = [];
  rest.split('|').forEach((raw) => {
    const option = raw.trim();
    if (!option) return;
    const key = option.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    options.push(option);
  });

  // 5. If options is empty -> NOT a choice (treat as Text).
  if (options.length === 0) return null;

  return { multi, allowOther, options };
}

export function isChoiceTypeHint(hint: string | undefined | null): boolean {
  return parseChoice(hint) !== null;
}

/** Writing form for builders (spec §1). */
export function formatChoiceTypeHint(choice: ParsedChoice): string {
  const kind = choice.multi ? 'Choices' : 'Choice';
  const other = choice.allowOther ? ', other' : '';
  return `(${kind}${other}: ${choice.options.join(' | ')})`;
}

/**
 * Format a set of selected option values (in the order the user picked them,
 * or any order) plus an optional "Other" free-text value into the canonical
 * stored answer string (spec §2):
 *   - Single: exactly one option string, verbatim (canonical casing).
 *   - Multi: selected options in DEFINITION order, joined with ", ".
 *   - Other (only if allowOther): "Other: <free text>". For multi, Other
 *     goes last: "A, Other: something".
 *   - Nothing selected and no other text: "" (unanswered).
 */
export function formatChoiceAnswer(
  choice: ParsedChoice,
  selected: string[],
  otherText?: string | null
): string {
  // Keep only options that are part of this question's definition, in
  // DEFINITION order (dedupe by case-insensitive match against the option).
  const selectedSet = new Set(selected.map((s) => s.toLowerCase()));
  const orderedSelected = choice.options.filter((option) => selectedSet.has(option.toLowerCase()));

  const trimmedOther = (otherText || '').trim();
  const hasOther = choice.allowOther && trimmedOther.length > 0;

  if (!choice.multi) {
    if (hasOther) return `Other: ${trimmedOther}`;
    return orderedSelected[0] || '';
  }

  const parts = [...orderedSelected];
  if (hasOther) parts.push(`Other: ${trimmedOther}`);
  return parts.join(', ');
}

export interface MatchedChoiceValue {
  /** Selected canonical option strings (definition casing), in definition order. */
  selected: string[];
  /** Free text following "Other: " if present, else undefined. */
  otherText?: string;
}

/**
 * Match a drafted/stored answer string against a choice's options to
 * pre-select chips (spec §5) — same tally-matching rule as §2: split the
 * answer on ", "; each part that equals an option (case-insensitive) counts
 * for that option; a part starting with "Other" (case-insensitive) is the
 * Other text; anything else is treated as Other text too.
 */
export function matchDraftedChoice(choice: ParsedChoice, value: string | undefined | null): MatchedChoiceValue {
  const result: MatchedChoiceValue = { selected: [] };
  if (!value) return result;

  const parts = String(value)
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  // Multi answers are joined with ", " (comma+space) per §2, but a single
  // answer with no comma is just one part — split(',') handles both since
  // options never contain commas.
  parts.forEach((part) => {
    const matchedOption = choice.options.find((option) => option.toLowerCase() === part.toLowerCase());
    if (matchedOption) {
      if (!result.selected.some((s) => s.toLowerCase() === matchedOption.toLowerCase())) {
        result.selected.push(matchedOption);
      }
      return;
    }

    if (/^other\s*:/i.test(part)) {
      result.otherText = part.replace(/^other\s*:\s*/i, '');
    } else if (choice.allowOther) {
      // Anything else counts as Other text too (tally rule §2).
      result.otherText = result.otherText ? `${result.otherText}, ${part}` : part;
    }
  });

  // Preserve definition order for selected options.
  result.selected = choice.options.filter((option) =>
    result.selected.some((s) => s.toLowerCase() === option.toLowerCase())
  );

  return result;
}
